import { Component, HostBinding, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import * as _ from 'underscore';
import { Endpoint, HelpIndex, ReviewSystem, SettingsType } from '../../../../models/enums';
import { ExportSettingsViewModel, ExportValidation } from '../../../../models/export-settings';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DrNamedQuery } from '../../../../models/dr-named-query';
import { KeyValueListComponent } from '../../../key-value-list/key-value-list.component';
import { ObjectActionListType, Permissions, ActionType } from '../../../../models/permission';
import { DrObject, SupportedRegExType } from '../../../../models/dr-object';
import { ExportComponent } from '../../../export/export.component';
import { ExportStreamOptions } from '../../../../models/export-stream-options';

@Component({
    standalone: false,
    selector: 'res-export-settings',
    templateUrl: './export-settings.component.html',
    styleUrls: ['./export-settings.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ExportSettingsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('errorPop', { static: true }) errorPop: PopoverDirective;
    @ViewChild('contentForm', { static: false }) contentForm: NgForm;
    @ViewChild(KeyValueListComponent, { static: false }) keyValueList: KeyValueListComponent;
    @ViewChild(ExportComponent, { static: false }) exportComponent: ExportComponent;

    exportDatabaseConnections: Array<{ handle: string; name: string }> = [];
    trackByHandle = (_: number, item: { handle: string }) => item.handle;

    isBusy = false;
    isBusyMessage: string;
    validChanges = true;
    helpIndex: HelpIndex;
    handle: string;
    viewModel: ExportSettingsViewModel;
    isProjectSettings: boolean;
    isSystemSettingsContext: boolean;
    drObject: ExportSettingsViewModel;
    isExportDataLoaded: boolean;
    isReefReviewEnabled: boolean;

    // permissions
    permissiontype: ObjectActionListType;
    hasCreatePermissions: boolean;
    isSystemUser = false;
    exportToTitle: string;

    // export form extensions
    exportSettingsOptions: ExportStreamOptions;
    isExportSettingsPage = true;
    private exportComponentIsDirty = false;

    settingsType: SettingsType;

    get isDirty(): boolean {
        if (this.contentForm) {
            return this.contentForm.dirty || this.keyValueList?.keyValueForm.form.dirty || this.exportComponentIsDirty;
        }
        return this.exportComponentIsDirty;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
                this.exportComponentIsDirty = false;
            }
        }
    }

    validation: ExportValidation = {
        drObject: new DrObject(),
        volumePrefix: {
            check: (): void => {
                if (this.viewModel.model.volumePrefix) {
                    this.validation.drObject.regex = SupportedRegExType.CONNECTOR;
                    const errorMessage = this.validation.drObject.uiValidateName(this.viewModel.model.volumePrefix);
                    this.validation.drObject.regex = SupportedRegExType.GENERIC;
                    if (errorMessage) {
                        this.validation.volumePrefix.errorMessage = errorMessage;
                        this.validation.volumePrefix.invalid = true;
                    } else {
                        this.validation.volumePrefix.invalid = false;
                    }
                } else {
                    this.validation.volumePrefix.invalid = true;
                    this.validation.volumePrefix.errorMessage = `Please specify Volume Label.`
                }
                this.isDirty = this.viewModel.isDirty();
            },
            invalid: false,
            errorMessage: ''
        },
        documentPrefix: {
            check: (): void => {
                if (this.viewModel.model.documentPrefix) {
                    this.validation.drObject.regex = SupportedRegExType.CONNECTOR;
                    const errorMessage = this.validation.drObject.uiValidateName(this.viewModel.model.documentPrefix);
                    this.validation.drObject.regex = SupportedRegExType.GENERIC;
                    if (errorMessage) {
                        this.validation.documentPrefix.errorMessage = errorMessage;
                        this.validation.documentPrefix.invalid = true;
                    } else {
                        this.validation.documentPrefix.invalid = false;
                    }
                } else {
                    this.validation.documentPrefix.invalid = true;
                    this.validation.documentPrefix.errorMessage = `Please specify Volume Label.`
                }
                this.isDirty = this.viewModel.isDirty();
            },
            invalid: false,
            errorMessage: ''
        },
        valid: (): boolean => {
            let invalid = false;
            this.validation.volumePrefix.check();
            invalid = invalid || this.validation.volumePrefix.invalid;
            this.validation.documentPrefix.check();
            invalid = invalid || this.validation.documentPrefix.invalid;
            return !invalid;
        }
    };

    constructor(private commonService: CommonService, private restService: RestService) { }

    loadSettingsData(handle, isDiscardChangesOperation?: boolean) {
        this.isBusyMessage = 'Loading Export Settings...';
        this.isBusy = true;
        this.exportSettingsOptions = null;
        this.restService.post(Endpoint.projectManager_getExportOptions, { projectHandle: handle, systemScope: this.isSystemUser }).then(response => {
            if (response) {
                this.isExportDataLoaded = true;
                delete response.numberResults;
                delete response.sessionToken;

                this.exportSettingsOptions = new ExportStreamOptions(response.exportSettings);
                this.exportSettingsOptions.allExportDatabaseConnections = response.exportSettings?.allExportDatabaseConnections || [];
                this.exportDatabaseConnections = response.exportSettings?.allExportDatabaseConnections ?? [];
                this.exportSettingsOptions.exportDatabaseConnectionHandle = response.exportSettings?.exportDatabaseConnectionHandle || '';
                this.exportSettingsOptions.folderSize = response.exportSettings.folderSize;
                this.exportSettingsOptions.maxRecordsPerFile = response.exportSettings.maxManifestRecords > 0;
                this.exportSettingsOptions.initialStartingDocumentId = response.exportSettings.selectedDocumentId;

                if (this.exportSettingsOptions) {
                    this.exportComponent?.handleExportOptions(this.exportSettingsOptions, true, isDiscardChangesOperation);
                }

                response.exportSettings.exportImageExceptionsSet = _.sortBy(
                    response.exportSettings.exportImageExceptionsSet,
                    (query: any) => query.name && query.name.toLowerCase()
                );
                /**
                 * Empty DrNamedQuery instance used to add a new named query to the list
                 * in the export settings dialog.
                 */
                const emptyNamedQuery = new DrNamedQuery({ name: '', query: '', description: null, annotationText: null });
                // Set up the response structure for the view model
                response.exportSettingsOptions = this.exportSettingsOptions;

                // Create view model once with the properly structured response
                this.viewModel = new ExportSettingsViewModel(response);

                this.viewModel.model.exportSettingsOptions.exportDatabaseConnectionHandle =
                    response.exportSettings?.exportDatabaseConnectionHandle ?? '';

                this.viewModel.model.exportSettingsOptions.postProcessKey =
                    response.exportSettings?.postProcessKey ?? undefined;
                this.viewModel.original.exportSettingsOptions.postProcessKey =
                    this.viewModel.model.exportSettingsOptions.postProcessKey;

                this.viewModel.model.exportSettingsOptions.exportImageExceptionsSet.push(new DrNamedQuery(emptyNamedQuery));
            }
            this.isBusy = false;
            this.isDirty = false;
        }, error => {
            this.isBusy = false;
        });
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.settingsType = settingsType;
        this.isSystemSettingsContext = settingsType === SettingsType.system;
        this.handle = node.data instanceof Template ? node.data.handle :
            projectHandle ? projectHandle : this.commonService.currentOrganizationHandle;
        this.isProjectSettings = node.data instanceof Template ? false : true;
        this.helpIndex = projectHandle ? HelpIndex.case_export_settings_dlg : (this.isSystemSettingsContext ? HelpIndex.sys_template_export_settings_dlg : HelpIndex.org_export_settings_template_dlg);
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        this.permissiontype = projectHandle ? ObjectActionListType.PRJ_EXPORT_SETTINGS : this.isSystemUser ? ObjectActionListType.SYSTEM_EXPORT_SETTINGS : ObjectActionListType.ORG_EXPORT_SETTINGS;
        this.hasCreatePermissions = Permissions.checkUserPermission(this.permissiontype, ActionType.CREATE);
        this.exportToTitle = this.commonService.currentProject?.reviewSystem === ReviewSystem.REEF_REVIEW ? 'Reef Review' : 'Relativity';
        this.loadSettingsData(this.handle);
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    saveChanges(fromAutoSave = false): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.isBusy = true;
            this.isBusyMessage = 'Validating Queries ...';

            this.keyValueList.validateQueries(this.keyValueList, this.viewModel.model.exportSettingsOptions.exportImageExceptionsSet).then(isQueriesValid => {
                if (!isQueriesValid) {
                    // If queries validation fails, resolve with false and return without saving the export settings
                    this.isBusy = false;
                    resolve(false);
                    return;
                }

                if (this.handle) {
                    const exportImageExceptionsSet = DrNamedQuery.removeEmptyQueries(this.viewModel.model.exportSettingsOptions.exportImageExceptionsSet);
                    this.viewModel.model.exportSettingsOptions.exportImageExceptionsSet = exportImageExceptionsSet;
                    this.viewModel.model.projectHandle = this.handle;
                    this.viewModel.model.selectedExportDatabaseConnection = undefined;
                    if (!this.exportComponent.exportOptions.maxRecordsPerFile) {
                        this.viewModel.model.exportSettingsOptions.maxManifestRecords = 0;
                    }

                    this.isBusyMessage = 'Saving Export Settings...';
                    this.isBusy = true;
                    const modelCopy: any = _.clone(this.viewModel.model);  // shallow copy, but we don't need a deep one here

                    if (modelCopy.exportSettingsOptions) {
                        modelCopy.exportSettings = modelCopy.exportSettingsOptions;
                        delete modelCopy.exportSettingsOptions; // Remove the frontend property
                        delete modelCopy.exportSettings.selectedExportDatabaseConnection;
                        delete modelCopy.exportSettings.lastUsedDBConnection;
                    }

                    if (modelCopy.externalParsingLibrary) {
                        delete modelCopy.externalParsingLibrary;
                    }
                    if (this.isSystemUser) {
                        Object.assign(modelCopy, { systemScope: true });
                    }

                    this.restService.post(Endpoint.projectManager_setExportSettings, modelCopy).then(() => {
                        this.isBusy = false;
                        this.isDirty = false;
                        this.keyValueList.keyValueForm.form.markAsPristine();

                        if (this.exportComponent && this.exportComponent.markAsClean) {
                            this.exportComponent.markAsClean();
                        }

                        if (!fromAutoSave) {
                            this.loadSettingsData(this.handle);
                        }
                        resolve(true);
                    }, () => {
                        this.isBusy = false;
                        resolve(false);
                    });
                } else {
                    resolve(false);
                }
            }).catch(error => {
                this.isBusy = false;
                reject(error);
            })
        });
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadSettingsData(this.handle, true);
        this.keyValueList.keyValueForm.form.markAsPristine();
        // validation.volumePrefix.invalid = false;
        this.validation.documentPrefix.invalid = false;
        if (this.exportComponent && this.exportComponent.markAsClean) {
            this.exportComponent.markAsClean();
        }
    }

    onChange() {
        this.isDirty = this.viewModel.isDirty();
    }

    isSaveDisabled(): boolean {
        let isEnabled = false;
        if (this.keyValueList) {
            const hasErrors = this.keyValueList.hasErrors();
            if (!this.isBusy && !hasErrors) {
                if (!this.validation.volumePrefix.invalid && !this.validation.documentPrefix.invalid) {
                    if (this.keyValueList.keyValueForm.form.dirty && this.keyValueList.valid) {
                        isEnabled = true;
                    } else if (!this.keyValueList.keyValueForm.form.dirty && this.isDirty) {
                        isEnabled = true;
                    }
                }
            }
        }
        if (!this.exportComponent?.valid()) {
            isEnabled = false;
        }
        return !isEnabled;
    }

    isDiscardDisabled(): boolean {
        let isEnabled = false;
        if (this.keyValueList) {
            if (!this.isBusy) {
                if (this.keyValueList.keyValueForm.form.dirty || this.isDirty) {
                    isEnabled = true;
                }
            }
        }
        return !isEnabled;
    }

    onExportDirtyStateChanged(isDirty: boolean): void {
        if (this.exportComponent) {
            if (this.viewModel) {
                // Update the model with current export options
                if (!this.viewModel.model?.exportSettingsOptions) {
                    this.viewModel.model.exportSettingsOptions = new ExportStreamOptions();
                }

                if (this.viewModel.model?.exportSettingsOptions) {
                    this.exportComponent.exportOptions.folderSize = this.viewModel.model.exportSettingsOptions.folderSize;
                    this.exportComponent.exportOptions.exportNearDupeNumericsIncluded = this.viewModel.model.exportSettingsOptions.exportNearDupeNumericsIncluded;
                    this.exportComponent.exportOptions.exportExtensionConversionEnabled = this.viewModel.model.exportSettingsOptions.exportExtensionConversionEnabled;
                    this.exportComponent.exportOptions.exportVolumesAllowed = this.viewModel.model.exportSettingsOptions.exportVolumesAllowed;
                    this.exportComponent.exportOptions.exportImageExceptionsSet = this.viewModel.model.exportSettingsOptions.exportImageExceptionsSet;
                    this.exportComponent.exportOptions.exportDatabaseConnectionHandle = this.viewModel.model.exportSettingsOptions.exportDatabaseConnectionHandle;
                    this.exportComponent.exportOptions.postProcessKey = this.viewModel.model.exportSettingsOptions.postProcessKey;
                    Object.assign(this.viewModel.model.exportSettingsOptions, this.exportComponent.exportOptions);
                }

                const isViewModelDirty = this.viewModel.isDirty();
                this.exportComponentIsDirty = isViewModelDirty;
                if (!isViewModelDirty) {
                    this.isDirty = false;
                }
            }
        }
    }
}
